import { analyzeFrames, getAuthorizedVideoUrl, importVideoFromUrl, moderateVideoFrames, uploadVideo } from '../services/api.js';
import { compressVideo, extractKeyFrames, getVideoDuration } from '../services/video-processor.js';
import { renderNavbar } from '../components/navbar.js';
import { supabase, isSupabaseReady } from '../services/supabase.js';
import { navigate } from '../router.js';
import { ANALYZE_FRAME_COUNT } from '../utils/constants.js';
import { computeVideoHash } from '../utils/video-hash.js';
import { formatMatchLabel } from '../utils/competitions.js';
import { escapeHtml } from '../utils/html.js';
import { ALLOWED_VIDEO_ACCEPT, MAX_ANALYSIS_SEGMENT_SECONDS, formatMB, validateSegment, validateVideoFile } from '../utils/upload-limits.js';
import { DEBRIEF_VISIBILITY, localizedDebriefVisibilityOptions } from '../utils/visibility.js';
import { t, tr } from '../services/i18n.js';

export function renderUpload(container) {
  if (!isSupabaseReady()) {
    container.innerHTML = `
      <div class="page app-shell">
        <header class="app-header">
          <div class="brand"><div class="brand-mark">FV</div><div><p class="brand-title">Fooview</p><p class="brand-subtitle">${t('brandSubtitle')}</p></div></div>
        </header>
        <div class="panel text-center">
          <p class="eyebrow">${t('configRequired')}</p>
          <h2 class="heading-2">${tr('Supabase doit être configuré pour uploader une vidéo.', 'Supabase must be configured to upload a video.')}</h2>
          <p class="text-muted mt-16">${tr('Ajoute VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY dans ton fichier .env.', 'Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your .env file.')}</p>
        </div>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="page app-shell">
      <header class="app-header">
        <div class="brand">
          <div class="brand-mark">FV</div>
          <div>
            <p class="brand-title">${tr('Nouvelle analyse', 'New analysis')}</p>
            <p class="brand-subtitle">${tr('Séquence courte · Question claire · Résultat vérifiable', 'Short clip · Clear question · Verifiable result')}</p>
          </div>
        </div>
        <a class="btn-secondary" href="#/home">← ${tr('Retour au feed', 'Back to feed')}</a>
      </header>

      <section class="upload-grid">
        <div class="panel">
          <p class="eyebrow">${tr('Question tactique', 'Tactical question')}</p>
          <h1 class="heading-2">${tr("Cadre ce que l'IA doit regarder.", 'Frame what the AI should watch.')}</h1>
          <p class="text-muted mt-16">${tr('Une bonne question évite les commentaires génériques. Exemple: pourquoi le pressing saute, quel joueur couvre mal, ou où se crée le surnombre.', 'A good question avoids generic comments. Example: why the press breaks, which player covers poorly, or where the overload appears.')}</p>
          <div class="mt-24">
            <label class="text-muted" style="display:block;margin-bottom:8px;">${tr('Question', 'Question')}</label>
            <input id="question" class="input-glow" type="text"
              placeholder="${tr('Pourquoi cette action amène une occasion ?', 'Why does this move create a chance?')}" maxlength="200" />
          </div>
          <div class="mt-24">
            <label class="text-muted" style="display:block;margin-bottom:8px;">${tr('Compétition / match', 'Competition / match')}</label>
            <select id="competition-select" class="input-glow" style="width:100%;margin-bottom:10px;">
              <option value="">${tr('Sans compétition', 'No competition')}</option>
            </select>
            <select id="match-select" class="input-glow" style="width:100%;">
              <option value="">${tr('Aucun match associé', 'No linked match')}</option>
            </select>
            <p id="competition-hint" class="text-muted" style="font-size:12px;margin:8px 0 0;">${tr('Optionnel : utile pour classements et défis CDM.', 'Optional: useful for rankings and World Cup challenges.')}</p>
          </div>
          <div class="mt-24">
            <label class="text-muted" style="display:block;margin-bottom:8px;">${tr('Visibilité', 'Visibility')}</label>
            <select id="visibility-select" class="input-glow" style="width:100%;">
              ${localizedDebriefVisibilityOptions().map((option) => `
                <option value="${escapeHtml(option.value)}" ${option.value === DEBRIEF_VISIBILITY.PUBLIC ? 'selected' : ''}>${escapeHtml(option.label)} - ${escapeHtml(option.badge)}</option>
              `).join('')}
            </select>
            <p id="visibility-hint" class="text-muted" style="font-size:12px;margin:8px 0 0;">${tr("Public par defaut. Tu pourras changer ce choix depuis l'analyse.", 'Public by default. You can change this choice from the analysis.')}</p>
          </div>
        </div>

        <div class="panel">
          <p class="eyebrow">${tr('Vidéo', 'Video')}</p>
          <div class="drop-zone" id="drop-zone">
            <div>
              <div style="font-size:28px; margin-bottom:12px; opacity:0.6;">🎬</div>
              <strong style="display:block; font-family:var(--font-ui); font-size:17px; font-weight:800; margin-bottom:8px; color:var(--ink); letter-spacing:-0.02em;">${tr('Déposer ou sélectionner une vidéo', 'Drop or select a video')}</strong>
              <p class="text-muted" style="font-size:13px;">${tr("Action nette · 30s max · jusqu'à 50 MB", 'Clear move · 30s max · up to 50 MB')}</p>
              <p id="video-status" class="text-muted" style="margin-top:14px; font-weight:700; color:var(--ink); font-size:13px;"></p>
            </div>
          </div>
          <div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:12px;">
            <button type="button" id="btn-pick-file" class="btn-secondary" style="flex:1;min-width:160px;">${tr('Choisir une vidéo', 'Choose a video')}</button>
            <button type="button" id="btn-pick-camera" class="btn-secondary" style="flex:1;min-width:160px;">${tr('Filmer avec la caméra', 'Record with camera')}</button>
          </div>
          <input id="video-input-file" type="file" accept="${ALLOWED_VIDEO_ACCEPT}" style="display:none;" />
          <input id="video-input-camera" type="file" accept="${ALLOWED_VIDEO_ACCEPT}" capture="environment" style="display:none;" />

          <div style="margin-top:14px;padding-top:14px;border-top:1px solid var(--line);">
            <label class="text-muted" style="display:block;margin-bottom:8px;">${tr('Lien vidéo ou réseau social', 'Video or social link')}</label>
            <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
              <input id="source-url" class="input-glow" type="url" inputmode="url"
                placeholder="${tr('Twitter/X, Bluesky ou lien MP4', 'Twitter/X, Bluesky, or MP4 link')}"
                style="flex:1;min-width:220px;" />
              <button type="button" id="btn-import-url" class="btn-secondary" style="min-width:120px;">${tr('Importer', 'Import')}</button>
            </div>
            <p id="source-url-hint" class="text-muted" style="font-size:12px;margin:8px 0 0;">${tr('Colle un lien public : Fooview récupère la vidéo puis garde le choix du segment.', 'Paste a public link: Fooview fetches the video, then keeps segment selection.')}</p>
          </div>

          <div id="segment-box" style="display:none;margin-top:16px;">
            <label class="text-muted" style="display:block;margin-bottom:8px;">${tr('Segment à analyser (secondes)', 'Segment to analyze (seconds)')}</label>
            <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
              <input id="seg-start" class="input-glow" type="number" min="0" step="0.5" value="0" style="flex:1;min-width:90px;" />
              <span class="text-muted">→</span>
              <input id="seg-end" class="input-glow" type="number" min="0" step="0.5" style="flex:1;min-width:90px;" />
            </div>
            <p id="seg-hint" class="text-muted" style="font-size:12px;margin:8px 0 0;">${tr(`Maximum ${MAX_ANALYSIS_SEGMENT_SECONDS}s analysées.`, `Maximum ${MAX_ANALYSIS_SEGMENT_SECONDS}s analyzed.`)}</p>
          </div>

          <div id="progress-container" style="display:none;margin-top:16px;" class="flex-col gap-12">
            <div class="progress-bar"><div id="progress-fill" class="progress-fill" style="width:0%"></div></div>
            <p id="progress-text" class="text-muted text-center"></p>
          </div>

          <button id="btn-analyze" class="btn-primary" disabled style="width:100%;margin-top:16px;">${tr("Lancer l'analyse", 'Start analysis')}</button>
        </div>
      </section>
    </div>
  `;

  renderNavbar(container);

  const questionInput = container.querySelector('#question');
  const competitionSelect = container.querySelector('#competition-select');
  const matchSelect = container.querySelector('#match-select');
  const competitionHint = container.querySelector('#competition-hint');
  const visibilitySelect = container.querySelector('#visibility-select');
  const dropZone = container.querySelector('#drop-zone');
  const videoStatus = container.querySelector('#video-status');
  const fileInput = container.querySelector('#video-input-file');
  const cameraInput = container.querySelector('#video-input-camera');
  const btnPickFile = container.querySelector('#btn-pick-file');
  const btnPickCamera = container.querySelector('#btn-pick-camera');
  const sourceUrlInput = container.querySelector('#source-url');
  const btnImportUrl = container.querySelector('#btn-import-url');
  const sourceUrlHint = container.querySelector('#source-url-hint');
  const btnAnalyze = container.querySelector('#btn-analyze');
  const progressContainer = container.querySelector('#progress-container');
  const progressFill = container.querySelector('#progress-fill');
  const progressText = container.querySelector('#progress-text');
  const segmentBox = container.querySelector('#segment-box');
  const segStart = container.querySelector('#seg-start');
  const segEnd = container.querySelector('#seg-end');
  const segHint = container.querySelector('#seg-hint');

  let selectedFile = null;
  let videoDuration = null;
  let importedVideoHash = null;
  let competitions = [];
  let matches = [];

  loadCompetitionOptions();

  // Valide la fenêtre temporelle. Renvoie {start, end} ou null si invalide.
  function readSegment() {
    const start = parseFloat(segStart.value);
    const end = parseFloat(segEnd.value);
    const result = validateSegment(start, end, videoDuration);
    segHint.textContent = result.message;
    return result.segment;
  }

  function refreshSegmentState() {
    const seg = readSegment();
    btnAnalyze.disabled = !selectedFile || !seg;
  }

  segStart.addEventListener('input', refreshSegmentState);
  segEnd.addEventListener('input', refreshSegmentState);

  // Ouvre un sélecteur de fichiers en réinitialisant la valeur pour autoriser
  // la re-sélection du même fichier ou un changement de source.
  function openPicker(input) {
    input.value = '';
    input.click();
  }

  // Le clic sur la zone principale ouvre les fichiers/galerie, jamais la caméra.
  dropZone.addEventListener('click', () => openPicker(fileInput));
  btnPickFile.addEventListener('click', () => openPicker(fileInput));
  btnPickCamera.addEventListener('click', () => openPicker(cameraInput));

  dropZone.addEventListener('dragover', (e) => { e.preventDefault(); dropZone.style.borderColor = 'var(--accent)'; });
  dropZone.addEventListener('dragleave', () => { dropZone.style.borderColor = ''; });
  dropZone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropZone.style.borderColor = '';
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  });

  fileInput.addEventListener('change', (e) => {
    if (e.target.files[0]) handleFile(e.target.files[0]);
  });
  cameraInput.addEventListener('change', (e) => {
    if (e.target.files[0]) handleFile(e.target.files[0]);
  });

  btnImportUrl.addEventListener('click', importFromSourceUrl);
  sourceUrlInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      importFromSourceUrl();
    }
  });

  async function handleFile(file) {
    const validation = await validateVideoFile(file).catch(() => ({
      valid: false,
      message: tr('Fichier vidéo illisible.', 'Video file cannot be read.')
    }));
    if (!validation.valid) {
      showToast(validation.message);
      return;
    }
    selectedFile = file;
    if (!file._fooviewVideoHash) importedVideoHash = null;
    // On garde la zone et les boutons en place pour pouvoir changer de source.
    videoStatus.textContent = tr(
      `${file.name} — ${formatMB(file.size)} sélectionnés`,
      `${file.name} — ${formatMB(file.size)} selected`
    );
    segmentBox.style.display = 'block';
    videoDuration = null;
    segStart.value = '0';
    segEnd.value = '';
    segHint.textContent = tr('Lecture de la durée...', 'Reading duration...');
    btnAnalyze.disabled = true;

    getVideoDuration(file).then((d) => {
      videoDuration = d > 0 ? d : null;
      const defaultEnd = videoDuration ? Math.min(videoDuration, MAX_ANALYSIS_SEGMENT_SECONDS) : MAX_ANALYSIS_SEGMENT_SECONDS;
      segEnd.value = defaultEnd.toFixed(1);
      if (videoDuration) {
        segStart.max = videoDuration;
        segEnd.max = videoDuration;
      }
      refreshSegmentState();
    }).catch(() => {
      // Durée inconnue : on autorise quand même une saisie manuelle.
      segEnd.value = String(MAX_ANALYSIS_SEGMENT_SECONDS);
      refreshSegmentState();
    });
  }

  async function importFromSourceUrl() {
    const url = sourceUrlInput.value.trim();
    if (!url) {
      showToast(tr('Colle un lien vidéo.', 'Paste a video link.'));
      return;
    }
    btnImportUrl.disabled = true;
    btnAnalyze.disabled = true;
    sourceUrlHint.textContent = tr('Récupération du flux vidéo...', 'Fetching video stream...');
    try {
      const imported = await importVideoFromUrl(url);
      const videoRes = await fetch(await getAuthorizedVideoUrl(imported.key));
      if (!videoRes.ok) throw new Error(tr('Vidéo importée introuvable.', 'Imported video not found.'));
      const blob = await videoRes.blob();
      const name = `${imported.source_platform || 'source'}-${Date.now()}.mp4`;
      const file = new File([blob], name, { type: blob.type || 'video/mp4' });
      file._fooviewVideoHash = imported.video_hash || null;
      importedVideoHash = file._fooviewVideoHash;
      await handleFile(file);
      sourceUrlHint.textContent = imported.extracted
        ? tr('Lien social importé. Choisis le segment à analyser.', 'Social link imported. Choose the segment to analyze.')
        : tr('Lien vidéo importé. Choisis le segment à analyser.', 'Video link imported. Choose the segment to analyze.');
    } catch (e) {
      showToast(tr('Import impossible: ', 'Import failed: ') + (e?.message || tr('lien non exploitable', 'unusable link')));
      sourceUrlHint.textContent = tr('Le lien doit être public et contenir un flux vidéo compatible.', 'The link must be public and contain a compatible video stream.');
      refreshSegmentState();
    } finally {
      btnImportUrl.disabled = false;
    }
  }

  btnAnalyze.addEventListener('click', async () => {
    if (!selectedFile) return;
    const segment = readSegment();
    if (!segment) {
      refreshSegmentState();
      return;
    }
    btnAnalyze.disabled = true;
    progressContainer.style.display = 'flex';

    try {
      updateProgress(10, tr('Compression de la vidéo...', 'Compressing video...'));
      const compressed = await compressVideo(selectedFile);

      // Hash SHA-256 de la vidéo compressée (avant upload) : permet au Worker de
      // dédupliquer le stockage et de réutiliser un résultat d'analyse identique.
      // Si Web Crypto échoue, videoHash reste null et on continue sans hash.
      const videoHash = (importedVideoHash && compressed === selectedFile)
        ? importedVideoHash
        : await computeVideoHash(compressed);

      updateProgress(30, tr('Découpage timeline détaillée...', 'Building detailed timeline...'));
      const { frames, frame_times, frame_previews, timeline_frames } = await extractKeyFrames(compressed, ANALYZE_FRAME_COUNT, segment);

      updateProgress(45, tr('Vérification de sécurité...', 'Running safety check...'));
      await moderateVideoFrames(frames, segment, frame_times, videoHash);

      updateProgress(55, tr('Upload vidéo...', 'Uploading video...'));
      const { key } = await uploadVideo(compressed, videoHash);

      updateProgress(70, tr(`Analyse tactique détaillée (${frames.length} frames)...`, `Detailed tactical analysis (${frames.length} frames)...`));
      const question = questionInput.value.trim() || tr('Analyse cette action tactiquement', 'Analyze this move tactically');
      // On transmet la video_key pour que le Worker vérifie que la vidéo uploadée
      // appartient bien à l'utilisateur (ou que la clé partagée correspond au
      // hash). L'analyse IA reste faite sur les frames.
      const analysis = await analyzeFrames(frames, question, segment, frame_times, key, videoHash);
      // Conserve la fenêtre choisie dans la réponse persistée (ai_raw_response).
      analysis.segment = segment;
      // Persiste les instants exacts et des aperçus légers des frames réellement
      // envoyées à l'IA : la page débrief les affiche directement, sans recapture
      // fragile depuis la vidéo (qui pouvait retomber sur le même keyframe).
      analysis.frame_times = frame_times;
      analysis.frame_previews = frame_previews;
      analysis.timeline_frames = timeline_frames;
      analysis.competition_id = competitionSelect.value || null;
      analysis.match_id = matchSelect.value || null;

      updateProgress(90, tr('Sauvegarde...', 'Saving...'));
      const { data: { user } } = await supabase.auth.getUser();
      const payload = {
        user_id: user.id,
        video_key: key,
        visibility: visibilitySelect.value || DEBRIEF_VISIBILITY.PUBLIC,
        competition_id: competitionSelect.value || null,
        match_id: matchSelect.value || null,
        title: analysis.title || tr('Analyse sans titre', 'Untitled analysis'),
        summary: analysis.summary || '',
        key_moment: analysis.key_moment || '',
        tactical_elements: analysis.tactical_elements || [],
        players_positions: analysis.players_positions || [],
        ball_position: analysis.ball_position || {},
        arrows: analysis.arrows || [],
        tactical_storyboard: analysis.tactical_storyboard || [],
        ai_raw_response: analysis
      };
      const { data: debrief, error } = await supabase
        .from('debriefs')
        .insert(payload)
        .select()
        .single();

      let saved = debrief;
      if (error && /competition_id|match_id|tactical_storyboard|column/i.test(error.message || '')) {
        delete payload.competition_id;
        delete payload.match_id;
        delete payload.tactical_storyboard;
        if (/visibility/i.test(error.message || '')) delete payload.visibility;
        const retry = await supabase.from('debriefs').insert(payload).select().single();
        if (retry.error) throw retry.error;
        saved = retry.data;
        showToast(tr('Débrief sauvegardé sans contexte match : migration Supabase à appliquer.', 'Debrief saved without match context: apply the Supabase migration.'));
      } else if (error) {
        throw error;
      }
      if (!saved?.id) throw new Error(tr('Debrief créé sans identifiant: navigation impossible.', 'Debrief created without an id: navigation impossible.'));

      updateProgress(100, tr('Debrief prêt.', 'Debrief ready.'));
      setTimeout(() => {
        navigate(`#/debrief/${saved.id}`);
      }, 800);
    } catch (e) {
      // Sort impérativement de l'état « Analyse tactique en cours... » : on masque
      // la barre de progression, on réinitialise et on réactive le bouton pour que
      // l'utilisateur puisse relancer après un timeout ou une erreur provider.
      progressContainer.style.display = 'none';
      updateProgress(0, '');
      showToast(tr('Erreur: ', 'Error: ') + (e?.message || tr('analyse impossible', 'analysis failed')));
      btnAnalyze.disabled = false;
    }
  });

  function updateProgress(pct, text) {
    progressFill.style.width = pct + '%';
    progressText.textContent = text;
  }

  function showToast(msg) {
    const t = document.createElement('div');
    t.className = 'toast show';
    t.style.color = 'var(--danger)';
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(() => { t.className = 'toast'; setTimeout(() => t.remove(), 300); }, 4000);
  }

  async function loadCompetitionOptions() {
    try {
      const [{ data: compData, error: compError }, { data: matchData, error: matchError }] = await Promise.all([
        supabase.from('competitions').select('*').eq('is_active', true).order('starts_at', { ascending: true }),
        supabase.from('matches').select('*').order('kickoff_at', { ascending: true })
      ]);
      if (compError || matchError) throw compError || matchError;
      competitions = compData || [];
      matches = matchData || [];
      competitionSelect.innerHTML = `<option value="">${tr('Sans compétition', 'No competition')}</option>` + competitions
        .map(c => `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}</option>`)
        .join('');
      refreshMatchOptions();
    } catch {
      competitionHint.textContent = tr('Compétitions indisponibles : migration Supabase à appliquer.', 'Competitions unavailable: apply the Supabase migration.');
      competitionSelect.disabled = true;
      matchSelect.disabled = true;
    }
  }

  function refreshMatchOptions() {
    const competitionId = competitionSelect.value;
    const scoped = matches.filter(m => !competitionId || m.competition_id === competitionId);
    matchSelect.innerHTML = `<option value="">${tr('Aucun match associé', 'No linked match')}</option>` + scoped
      .map(m => `<option value="${escapeHtml(m.id)}">${escapeHtml(formatMatchLabel(m))}</option>`)
      .join('');
  }

  competitionSelect.addEventListener('change', refreshMatchOptions);
}
